import { useEffect, useState } from "react";

// Parses "(GMT+01:00) British Summer Time (BST)" -> 60 (minutes)
// Also handles "(GMT±00:00) ..." and "(GMT-03:30) ..."
const parseOffsetMinutes = tz => {
  if (!tz || typeof tz !== "string") return null;

  const match = tz.match(/^\(GMT([+\-±])(\d{1,2}):(\d{2})\)/);
  if (!match) return null;

  const sign = match[1] === "-" ? -1 : 1; // "+" and "±" (±00:00) are both non-negative
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
};

// Fallback: if the value is a real IANA name like "Asia/Kolkata"
const getIanaOffsetMinutes = (utcMs, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date(utcMs));

    const get = type => parseInt(parts.find(p => p.type === type).value, 10);
    const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));

    return Math.round((asUTC - Math.floor(utcMs / 1000) * 1000) / 60000);
  } catch (e) {
    return null; // not a valid IANA name
  }
};

const getEndTimestamp = config => {
  const [hh, mm] = config.endTime.split(":");
  let hours = parseInt(hh, 10);
  const minutes = parseInt(mm, 10);

  if (config.ampm === "PM" && hours !== 12) hours += 12;
  if (config.ampm === "AM" && hours === 12) hours = 0;

  const [year, month, day] = config.endDate.split("-").map(Number);

  // the end time as if it were UTC
  const wallClockAsUtc = Date.UTC(year, month - 1, day, hours, minutes, 0);

  // 1. "(GMT+01:00) ..." label from your timezone dropdown
  let offsetMin = parseOffsetMinutes(config.timezone);

  // 2. real IANA name (e.g. "Europe/London")
  if (offsetMin === null && config.timezone) {
    const first = getIanaOffsetMinutes(wallClockAsUtc, config.timezone);
    if (first !== null) {
      const guess = wallClockAsUtc - first * 60000;
      const second = getIanaOffsetMinutes(guess, config.timezone); // DST boundary check
      offsetMin = second !== null ? second : first;
    }
  }

  // 3. unknown timezone -> browser local time
  if (offsetMin === null) {
    return new Date(year, month - 1, day, hours, minutes, 0).getTime();
  }

  // local wall-clock time minus the zone's offset = real UTC moment
  return wallClockAsUtc - offsetMin * 60000;
};

const useCountdown = config => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  let d = 0,
    h = 0,
    m = 0,
    s = 0;

  if (config.endDate && config.endTime) {
    const diff = getEndTimestamp(config) - now;
    const totalSec = Math.max(0, Math.floor(diff / 1000));

    d = Math.floor(totalSec / 86400);
    h = Math.floor((totalSec % 86400) / 3600);
    m = Math.floor((totalSec % 3600) / 60);
    s = totalSec % 60;
  }

  return { d, h, m, s };
};

export default useCountdown;
